/**
 * Reference Estimate Corpus - canonical structure
 * ───────────────────────────────────────────────
 * The normalized form of a customer's historical "improvement estimator" (or any
 * priced reference estimate): a list of scoped items, each with a free-form
 * functional area and a VARIABLE-length `bands` array of effort figures. Bands are
 * intentionally not fixed to low/med/high - the customer's own column labels become
 * the band vocabulary (one customer uses Low/Med/High, another P10/P50/P90), and an
 * optional `percentile` is the bridge to the system's estimate-range concept.
 *
 * One uploaded file -> one `ReferenceEstimateSet`, persisted as the `metadata` of a
 * `knowledge.document` (sourceKind='reference_estimate') and chunked per item into
 * `ai.content_chunk` for retrieval. See packages/ai reference-corpus + grounding.
 *
 * The `*Normalize` variant (sans server-stamped fields) is what the LLM-fallback
 * parser produces via `generateValidated`; the deterministic Excel extractor builds
 * the full set directly. Kept PERMISSIVE with no `.refine()`/cross-field rules so the
 * runtime can feed `formatZodIssues` back to the model for self-correction.
 */
import { z } from 'zod';
import { scopeMetaSchema } from '../types/scope.js';
import { slugify } from '../utils/slug.js';
/**
 * The estimator-relevant slice of the scope topology, extracted from each past item at INGEST and
 * stored on `ReferenceEstimateItem.scope` (in document.metadata, recovered by chunkIndex - same as
 * `bands`). It is NOT embedded; the chunk text stays the original prose. The estimator reads it to
 * warm-start cold inference with topology-tagged, like-for-like exemplars. Reuses the shared
 * {@link scopeMetaSchema} coercers (trim/cap/enum-clamp, never throws) so corpus and deal items
 * speak one vocabulary. Optional everywhere - sets imported before this feature simply lack it.
 */
export const referenceEstimateScopeSchema = scopeMetaSchema.pick({
    workType: true,
    complexityDrivers: true,
    problem: true,
    proposedSolution: true,
    inScope: true,
    outOfScope: true,
    size: true,
});
/** One effort figure for an item under a named band (e.g. 'low'/'med'/'high' or 'p50'). */
export const referenceEstimateBandSchema = z.object({
    /** Band identifier, taken from the source column label. */
    key: z.string().min(1),
    label: z.string().optional(),
    /** Effort in hours for this band. */
    hours: z.number().nonnegative(),
    /** Optional percentile [0,100] when the source expresses bands as percentiles. */
    percentile: z.number().min(0).max(100).optional(),
});
/** One scoped line item: a functional area + description + variable effort bands. */
export const referenceEstimateItemSchema = z.object({
    /**
     * The functional area this item belongs to, e.g. "Order to Cash" / "Month End Close Optimization".
     * Functional area ≡ business process: this is the SAME concept as the deal side's
     * `ScopeMeta.businessProcess` (kept under two names by decision) - the coarse grouping key that
     * `loadTaxonomyInventory` feeds into deal grouping and that harmonization groups on. The raw column
     * value the parser first reads is brittle/inconsistent across workbooks; when
     * `ai.reference_functional_area_detection_enabled` is on, an AI judge re-derives a robust,
     * taxonomy-consistent value at ingest (see `extractReferenceTopology`). The fine deliverable detail
     * lives in `description`.
     */
    functionalArea: z.string().min(1),
    /**
     * The RAW business-process column value as parsed from the source workbook, preserved verbatim
     * whenever FA detection rewrites `functionalArea` - so the firm's own vocabulary ("Record to
     * Report", "R2R") is never lost to a detector-coined name and the corpus can always be
     * re-normalized against a (later) canonical list. Absent when detection never changed the value
     * or on sets imported before this field.
     */
    rawFunctionalArea: z.string().optional(),
    description: z.string().default(''),
    /** Variable-length effort bands (at least one); NEVER assume low/med/high. */
    bands: z.array(referenceEstimateBandSchema).min(1),
    /** Free-form timing/phase note from the source, e.g. "Q2". */
    timing: z.string().optional(),
    assumptions: z.string().optional(),
    risks: z.string().optional(),
    notes: z.string().optional(),
    tags: z.array(z.string()).optional(),
    /**
     * The parent SECTION / phase label this item sits under in the source estimate (e.g. "Record to
     * Report"). Captured at parse so the estimate's structure can be reconstructed - group items by
     * section, in array order - to scaffold a reusable workspace template. Optional: flat sheets and
     * sets parsed before this feature have none.
     */
    section: z.string().optional(),
    /**
     * Structured scope topology extracted from this item at ingest (see
     * {@link referenceEstimateScopeSchema}). Optional - absent on sets imported before this feature.
     */
    scope: referenceEstimateScopeSchema.optional(),
});
/** The ordered band vocabulary detected for a set (from the source column labels). */
export const referenceEstimateBandDefSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    percentile: z.number().min(0).max(100).optional(),
});
/** Supported sources; xlsx ships first, docx/pdf reuse the same normalize step. 'deal' marks a set
 *  promoted from a settled deal's final estimate (no source file - see sourceDealUuid). */
export const referenceEstimateFormatSchema = z.enum(['xlsx', 'docx', 'pdf', 'deal']);
/** One uploaded reference-estimate file, fully normalized. */
export const referenceEstimateSetSchema = z.object({
    /** The stored asset (GCS) the set was parsed from. Stamped server-side. Null for sets not parsed
     *  from an uploaded file (sourceFormat 'deal'). */
    sourceAssetUuid: z.string().nullable(),
    /** The source file format. Stamped server-side (known by the parser). */
    sourceFormat: referenceEstimateFormatSchema,
    title: z.string().min(1),
    /** Deal category this corpus grounds, e.g. 'continuous_improvement'. */
    categoryKey: z.string().optional(),
    /** Product key, e.g. 'oracle:netsuite'. */
    productKey: z.string().optional(),
    /**
     * Provenance captured at import (workspace-admin wizard): the customer/company this past estimate
     * was for, and an optional deal/engagement label. Used to label the citation drill-in so the user
     * knows where a grounded figure came from. Optional - absent on sets imported before this feature
     * (they fall back to {@link sourceFileName} then {@link title}; see {@link referenceEstimateLabel}).
     */
    companyName: z.string().optional(),
    dealName: z.string().optional(),
    /** Original uploaded file name. Stamped server-side at parse; a label fallback when company is unset. */
    sourceFileName: z.string().optional(),
    /** The deal this set was promoted from (sourceFormat 'deal'): dedup key for re-promotion and the
     *  leave-one-out exclusion handle when that same deal is used as a tuning golden. */
    sourceDealUuid: z.string().optional(),
    bandDefs: z.array(referenceEstimateBandDefSchema).optional(),
    items: z.array(referenceEstimateItemSchema),
});
/** Strip "Copy of " prefixes, the extension, and underscore separators from an upload file name. */
export function cleanReferenceFileName(fileName) {
    if (!fileName)
        return '';
    return fileName
        .trim()
        .replace(/\.(xlsx|xls|docx|doc|pdf|csv)$/i, '')
        .replace(/^\s*copy of\s+/i, '')
        .replace(/_+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
}
/**
 * Human provenance label for a reference set - the citation drill-in title and the grounding source
 * label. Precedence: explicit "Company - Deal" captured at import > cleaned source filename > the
 * parsed sheet title (often weak, e.g. a tab name like "Summary"). Never empty.
 */
export function referenceEstimateLabel(set) {
    const company = set.companyName?.trim();
    const deal = set.dealName?.trim();
    if (company)
        return deal ? `${company} - ${deal}` : company;
    if (deal)
        return deal;
    const fromFile = cleanReferenceFileName(set.sourceFileName);
    if (fromFile)
        return fromFile;
    return set.title?.trim() || 'Reference estimate';
}
/**
 * Title-case-ish humanize of a key/segment for display: "continuous_improvement" -> "Continuous
 * Improvement", "oracle:netsuite" -> "Netsuite". Preserves existing inner capitals ("NetSuite" stays
 * "NetSuite"); only upper-cases each word's first letter.
 */
export function humanizeReferenceKey(key) {
    if (!key)
        return '';
    const seg = key.includes(':') ? key.slice(key.lastIndexOf(':') + 1) : key;
    return seg
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}
/**
 * Best-effort COMPANY name from an upload file name. We don't parse positionally (file names are
 * inconsistent: "Copy of MentorcliQ - NetSuite CI 8-26-2025", "Aug 26 2025 - RCP - Aetion ..."); we
 * instead remove the parts we ALREADY know - the product + category words + dates - and keep the
 * leading run of what remains (so the product/category we collected at import isolate the company).
 * Returns '' when nothing usable remains (the label then falls back to the deal/filename/title).
 */
export function inferCompanyFromFileName(fileName, knownLabels) {
    const cleaned = cleanReferenceFileName(fileName)
        .replace(/-+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        // Re-strip a leading "Copy of" now that hyphen separators are spaces (the file-name clean only
        // catches the already-spaced form, not a sanitized "Copy-of-...").
        .replace(/^\s*copy of\s+/i, '')
        .trim();
    if (!cleaned)
        return '';
    const strip = new Set(knownLabels
        .filter((s) => !!s)
        .flatMap((s) => s.split(/[\s:_-]+/))
        .map((w) => w.toLowerCase())
        .filter((w) => w.length > 1));
    const isNoise = (w) => /^\d+$/.test(w) ||
        /^\d{1,2}[-/.]\d{1,2}([-/.]\d{2,4})?$/.test(w) ||
        /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*$/.test(w);
    const kept = [];
    for (const raw of cleaned.split(/\s+/)) {
        const w = raw.toLowerCase().replace(/[(),.]/g, '');
        if (!w)
            continue;
        // A product/category/date word ENDS the company run once it has started; before that, skip it
        // (handles leading dates / consultancy prefixes that precede the company).
        if (isNoise(w) || strip.has(w)) {
            if (kept.length > 0)
                break;
            continue;
        }
        kept.push(raw);
        if (kept.length >= 3)
            break;
    }
    return kept.join(' ').trim();
}
/**
 * The shape the LLM-fallback parser must produce: the full set minus the two fields
 * the model cannot know (the asset uuid and the file format), which the parse tool
 * stamps + re-validates server-side. Registered in the SchemaRegistry for
 * `generateValidated` / the `validate_schema` tool.
 */
export const referenceEstimateNormalizeSchema = referenceEstimateSetSchema.omit({
    sourceAssetUuid: true,
    sourceFormat: true,
    // Provenance is captured by the import wizard / stamped from the file, never inferred by the model.
    companyName: true,
    dealName: true,
    sourceFileName: true,
});
/**
 * Map a parsed reference estimate's STRUCTURE into a {@link CategoryQuestionPlan} - the input that
 * materializes a workspace `deal_template` (section -> group -> field). Items are grouped by their
 * `section` (first-occurrence order, preserving item order); each section becomes a plan section with
 * one group, and each line item becomes a scope FIELD. Items without a `section` fall under a single
 * "General" section. Field keys are de-duped across the whole plan (template keys must be unique).
 * Returns null when there are no items. Pure - unit-testable.
 */
export function referenceStructureToQuestionPlan(set) {
    const items = set.items ?? [];
    if (items.length === 0)
        return null;
    const order = [];
    const bySection = new Map();
    for (const it of items) {
        const section = it.section?.trim() || 'General';
        let arr = bySection.get(section);
        if (!arr) {
            arr = [];
            bySection.set(section, arr);
            order.push(section);
        }
        arr.push(it);
    }
    const usedFieldKeys = new Set();
    const sections = order.map((sectionLabel) => {
        const sectionKey = slugify(sectionLabel) || 'section';
        const fields = (bySection.get(sectionLabel) ?? []).map((it) => {
            const base = slugify(it.functionalArea) || 'item';
            let fieldKey = base;
            let n = 2;
            while (usedFieldKeys.has(fieldKey))
                fieldKey = `${base}_${n++}`;
            usedFieldKeys.add(fieldKey);
            const description = it.description?.trim() || undefined;
            return {
                fieldKey,
                label: it.functionalArea,
                uiControl: 'textarea',
                ...(description ? { description } : {}),
                isRequired: false,
                aiEnabled: true,
                aiPrompt: `Capture the scope and effort for "${it.functionalArea}"${description ? ` - ${description}` : ''} for this deal.`,
            };
        });
        return {
            sectionKey,
            label: sectionLabel,
            groups: [{ groupKey: `${sectionKey}_scope`, label: sectionLabel, fields }],
        };
    });
    return { sections };
}
//# sourceMappingURL=reference-estimate.js.map